import { computed, type InjectionKey } from "vue";
import type {
  AddToCartRequest,
  OrderApi,
  OrderContactPostRequest,
  OrderPatchRequest,
  ReferrerCodeAddRequest,
  RemoveFromCartRequest,
  VoucherCodeAddRequest,
} from "./order_api";
import { CurrentOrderProvider } from "../current_order_provider";

export class OrderService {
  private readonly orderApi: OrderApi;
  private readonly currentOrderProvider: ReturnType<
    typeof CurrentOrderProvider
  >;

  constructor(
    orderApi: OrderApi,
    currentOrderProvider: ReturnType<typeof CurrentOrderProvider>,
  ) {
    this.orderApi = orderApi;
    this.currentOrderProvider = currentOrderProvider;
  }

  async orderUpdate(
    billing_type: string|null,
    billing_period: string|null,
    source_id: number|null,
  ) {
    const request: OrderPatchRequest = {};
    if (null != billing_type) {
      request.billing_type = billing_type;
    }
    if (null != billing_period) {
      request.billing_period = billing_period;
    }
    if (null != source_id) {
      request.source_id = source_id;
    }
    const orderReference =
      this.currentOrderProvider?.currentOrder?.value?.order_reference || "";
    const resp = await this.orderApi.updateOrder(orderReference, request);
    if (resp) {
      this.currentOrderProvider.set(resp.order);
      this.currentOrderProvider.setVariants(resp.available_variants);
    }
  }

  async addToCart(
    productVariantId: number,
    reference: string,
    quantity: number,
  ) {
    const request: AddToCartRequest = {
      product_variant_id: productVariantId,
      reference: reference,
      quantity: quantity,
    };
    const orderReference =
      this.currentOrderProvider?.currentOrder?.value?.order_reference || "";
    const resp = await this.orderApi.addToCart(orderReference, request);
    if (resp) {
      this.currentOrderProvider.set(resp.order);
      this.currentOrderProvider.setVariants(resp.available_variants);
    }
  }

  async removeFromCart(
    productVariantId: number,
    reference: string,
    quantity: number,
  ) {
    const request: RemoveFromCartRequest = {
      product_variant_id: productVariantId,
      reference: reference,
      quantity: quantity,
    };
    const orderReference =
      this.currentOrderProvider?.currentOrder?.value?.order_reference ?? "";
    const resp = await this.orderApi.removeFromCart(orderReference, request);
    if (resp) {
      this.currentOrderProvider.set(resp.order);
      this.currentOrderProvider.setVariants(resp.available_variants);
    }
  }

  async patchConfig(
    configId: string,
    configType: string,
    request: object,
  ) {
    const orderReference =
      this.currentOrderProvider?.currentOrder?.value?.order_reference ?? "";
    const resp = await this.orderApi.patchConfig(
      orderReference,
      configType,
      configId,
      request,
    );
    if (resp) {
      this.currentOrderProvider.set(resp.order);
      this.currentOrderProvider.setVariants(resp.available_variants);
    }
  }

  async postAddress(
    addressType: string,
    request: OrderContactPostRequest | null,
  ) {
    const orderReference =
      this.currentOrderProvider?.currentOrder?.value?.order_reference ?? "";
    const resp = await this.orderApi.postAddress(
      orderReference,
      addressType,
      request,
    );
    if (resp) {
      this.currentOrderProvider.set(resp.order);
      this.currentOrderProvider.setVariants(resp.available_variants);
    }
  }

  async addVoucher(voucherCode: string | null) {
    const request: VoucherCodeAddRequest = {
      voucher_code: voucherCode,
    };
    const orderReference =
      this.currentOrderProvider?.currentOrder?.value?.order_reference ?? "";
    const resp = await this.orderApi.addVoucher(orderReference, request);
    if (resp) {
      this.currentOrderProvider.set(resp.order);
      this.currentOrderProvider.setVariants(resp.available_variants);
    }
  }

  async addReferrer(referrerCode: string | null) {
    const request: ReferrerCodeAddRequest = {
      referrer_code: referrerCode,
    };
    const orderReference =
      this.currentOrderProvider?.currentOrder?.value?.order_reference ?? "";
    const resp = await this.orderApi.addReferrer(orderReference, request);
    if (resp) {
      this.currentOrderProvider.set(resp.order);
      this.currentOrderProvider.setVariants(resp.available_variants);
    }
  }

  async submit() {
    const orderReference =
      this.currentOrderProvider?.currentOrder?.value?.order_reference ?? "";
    const resp = await this.orderApi.submit(orderReference);
    if (resp) {
      // Bestellung ist abgeschlossen: aktuelle Bestellung samt Cookie löschen
      this.currentOrderProvider.set(null);
      this.currentOrderProvider.setVariants([]);
    }
  }

  readonly currentVatType = computed(() => {
      return this.currentOrderProvider?.currentOrder?.value?.vat_type ?? "inclusive";
  })

  readonly currentItems = computed(() => {
      return this.currentOrderProvider?.currentOrder?.value?.items ?? [];
  })

  readonly availableVariants = computed(() => {
      return this.currentOrderProvider?.availableVariants?.value ?? [];
  })
}

export const orderServiceKey: InjectionKey<OrderService> =
  Symbol("order-service");
